import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Mail, X, Send, CheckCircle } from 'lucide-react';
import { submitFeedback } from '../../services/feedbackService';

const ContactSupportButton = (
  {
  // Button customization
  buttonText = "Need Help? Please Contact Support",
  buttonTextColor = "text-white",
  buttonHoverColor = "hover:text-blue-800",
  buttonIcon = Mail,
  buttonIconColor = "yellow",
  buttonIconSize = 16,
  buttonClassName = ""
}
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      message: ''
    }
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await submitFeedback(data);

      if (response?.success) {
        setIsSubmitted(true);
        setTimeout(() => {
          reset();
          setIsSubmitted(false);
          setIsOpen(false);
        }, 2000);
      } else {
        throw new Error(response?.message || 'Unknown error');
      }

    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const ButtonIcon = buttonIcon;

  return (
    <>
      {/* Inline "Contact Support" button */}
      {/* <button
        onClick={() => setIsOpen(true)}
        className="text-white hover:text-blue-800 underline flex items-center gap-3 text-sm"
      >
        <Mail color='yellow' size={16} />
        Need Help? Please Contact Support
      </button> */}
       <button
        onClick={() => setIsOpen(true)}
        className={`${buttonTextColor} ${buttonHoverColor} underline flex items-center gap-3 text-sm ${buttonClassName}`}
      >
        <ButtonIcon color={buttonIconColor} size={buttonIconSize} />
        {buttonText}
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b">
              <h2 className="text-xl font-semibold text-black">Feedback Form</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-black hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Form Content */}
            <div className="p-6">
              {isSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-black mb-2">Thank you!</h3>
                  <p className="text-gray-600">Your feedback has been submitted successfully.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-black mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      {...register('name', {
                        required: 'Name is required',
                        minLength: { value: 2, message: 'Name must be at least 2 characters long' },
                        pattern: {
                          value: /^[A-Za-z\s]+$/,
                          message: 'Name can only contain letters and spaces'
                        }
                      })}
                      placeholder="Enter your name"
                      className={`text-black w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-black mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Please enter a valid email address'
                        }
                      })}
                      placeholder="Enter your email"
                      className={`text-black w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>}
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-black mb-1">
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={4}
                      {...register('message', {
                        required: 'Message is required',
                        minLength: {
                          value: 10,
                          message: 'Message must be at least 10 characters long'
                        },
                        maxLength: {
                          value: 500,
                          message: 'Message cannot exceed 500 characters'
                        }
                      })}
                      placeholder="Your feedback"
                      className={`text-black w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                        errors.message ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.message && (
                      <p className="text-red-600 text-sm mt-1">{errors.message.message}</p>
                    )}
                  </div>

                  {/* Error message */}
                  {error && (
                    <div className="text-red-600 text-sm bg-red-50 p-2 rounded-md">
                      {error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="button"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Submit Feedback
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactSupportButton;
